import React, { useMemo } from 'react';
import i18next from 'i18next';
import { observer } from 'mobx-react';
import { Radio } from 'components';

import { routerStore } from 'core';
import {
  FormWrapper,
  WrappedInput,
  FormField,
  InputWrapper,
} from 'libs/forms/styles';
import { clientStore } from 'apps/client/app';
import { isTrialExpired } from 'utils/freeTrials';
import {
  CreditCard,
  SubscriptionManager,
} from '../../onboarding/subscription/subscriptionManager';
import { CardLabel, NewCardForm } from '../method/newCardForm';

import { CreditCard as CreditCardView } from './creditCard';

export interface AddPaymentDetailsProps {
  seats: number;
  cardId?: string;
  productType: 'pro' | 'business';
  billingCycle: 'yearly' | 'monthly';
  componentMode: 'edit' | 'readOnly';
  annualPriceDetails?: {
    unsavedPercentage: number;
    originPrice: number;
  };
  creditCards?: CreditCard[];
  totalPrice: number;

  onBillingCycleChange?: (billingCycle: 'yearly' | 'monthly') => void;
  onCreditCardSelected?: (cardId: string) => void;
  successAction?: () => void;
  onSetAddCardFormVisible?: (show: boolean) => void;
  addCardFormVisible: boolean;
  showAddPayment?: boolean;
  startButton?: React.ReactNode;
  submitText?: string;
  loading?: boolean;
  isStartFreeTrial?: boolean;

  // pass the whole manager here is really bad, but since it's in need for newCardForm
  manager: SubscriptionManager;
}

export const AddPaymentDetails = observer((props: AddPaymentDetailsProps) => {
  const {
    loading,
    componentMode,
    manager,
    creditCards,
    productType,
    onCreditCardSelected,
    successAction,
    startButton,
    submitText,
    onSetAddCardFormVisible,
    addCardFormVisible,
    showAddPayment,
    cardId,
    isStartFreeTrial,
  } = props;

  const hasCreditCards = !!creditCards?.length;
  const trialTimeExisted = !!clientStore.currentSpaceData.trialPeriodEnd;
  const trialExpired =
    trialTimeExisted &&
    isTrialExpired(clientStore.currentSpaceData.trialPeriodEnd);

  const addPaymentFormTitle = (
    <h1 className=' text-2xl text-black font-extrabold'>Add Payment Details</h1>
  );

  const isUpgrade = routerStore.route.name === 'workspace.upgrade';
  const isChangePlan = isUpgrade && productType !== 'pro';

  const CreateCardFrom = ({ hideNote }: { hideNote?: boolean }) => (
    <NewCardForm
      saveProps={{
        size: 'default',
        disabled: !hasCreditCards,
        text: i18next.t(submitText),
        dataTestId: 'reviewPlanPage-startFreeTrial-button',
        icon: 'arrow_right',
        iconPosition: 'right',
        loading: manager.submitting || loading,
      }}
      footer={
        !isChangePlan && (
          <p className='text-black text-center mt-3'>You can cancel anytime</p>
        )
      }
      hideDivider
      successAction={() => {
        if (!isChangePlan) {
          successAction();
        }
      }}
      renderCardLogo={false}
      showFormTitle={false}
      expandStripeTop={(submit: any, adding: boolean) => (
        <>
          {componentMode !== 'edit' && productType !== 'pro' && (
            <div className='w-full'>
              {!addCardFormVisible && (
                <span
                  className='text-blue-500 cursor-pointer'
                  onClick={() => {
                    onSetAddCardFormVisible(true);
                  }}
                  data-testid='paymentDetailPage-addPaymentMethod-button'
                >{`+${i18next.t('Add New Payment Method')}`}</span>
              )}

              {addCardFormVisible && (
                <div className='flex justify-between items-center'>
                  {addPaymentFormTitle}
                  <div className='flex justify-end items-center'>
                    {adding ||
                    manager.submitting ||
                    manager.newCard?.submitting ? (
                      <span className='cursor-default'>Adding</span>
                    ) : (
                      <span
                        className='text-blue-500 cursor-pointer'
                        onClick={() => {
                          submit();
                        }}
                        data-testid='paymentDetailPage-confirmAddPaymentMethod-button'
                      >
                        Add
                      </span>
                    )}
                    {hasCreditCards && (
                      <>
                        <div className='h-4 w-px mx-2 bg-black' />
                        <span
                          className='cursor-pointer hover:text-red-500'
                          onClick={() => {
                            onSetAddCardFormVisible(false);
                          }}
                          data-testid='paymentDetailPage-cancelAddPaymentMethod-button'
                        >
                          Cancel
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      expandStripeBottom={() => {
        return (
          isStartFreeTrial && (
            <div>
              <div style={{ paddingTop: '15px' }}>
                <FormWrapper noPadding responsive>
                  <FormField>
                    <CardLabel>
                      Tax ID
                      <span className='ml-2 text-14 font-light'>
                        (Optional)
                      </span>
                    </CardLabel>

                    <InputWrapper
                      padding='7px 10px'
                      responsive
                      style={{ borderRadius: '2px' }}
                    >
                      <WrappedInput
                        style={{ marginTop: 0 }}
                        data-testid='paymentDetailPage-taxId-input'
                        onChange={(e: Record<string, any>) => {
                          manager.OnTaxIdChange(e.target.value);
                        }}
                        value={manager.taxId}
                        placeholder='A 0000 0000 0000'
                      />
                    </InputWrapper>
                  </FormField>
                </FormWrapper>
              </div>
            </div>
          )
        );
      }}
      hideNote={hideNote}
      hideAddButton={componentMode !== 'edit' && productType !== 'pro'}
      paymentMethodsManager={manager}
      responsive
    />
  );

  const AddPaymentBar = (
    <div className='w-full'>
      {!addCardFormVisible && (
        <span
          className='text-blue-500 cursor-pointer'
          onClick={() => {
            onSetAddCardFormVisible(true);
          }}
          data-testid='paymentDetailPage-addPaymentMethod-button'
        >{`+${i18next.t('Add New Payment Method')}`}</span>
      )}
      {/* {!addCardFormVisible && (
        <span
          className='text-blue-500 cursor-pointer'
          onClick={() => {
            onSetAddCardFormVisible(true);
          }}
        >{`+${i18next.t('Add New Payment Method')}`}</span>
      )}

      {addCardFormVisible && (
        <div className='flex justify-end items-center'>
          <Button
            dataTestId='paymentDetailPage-addPaymentMethod-button'
            text={i18next.t('Add')}
            variant='tertiary'
            size='small'
            onClick={() => {
              onConfirmPaymentMethod();
            }}
          />
          <span
            className='inline-block ml-4 cursor-pointer'
            onClick={() => {
              onSetAddCardFormVisible(false);
            }}
          >
            Cancel
          </span>
        </div>
      )} */}
    </div>
  );

  const shouldShowPaymentCardForm =
    componentMode === 'edit' || productType === 'pro' || addCardFormVisible;

  const paymentCardForm = useMemo(() => {
    if (shouldShowPaymentCardForm) {
      return <CreateCardFrom />;
    }

    return null;
  }, [shouldShowPaymentCardForm]);

  const paymentCards = (
    <>
      <div className='my-4'>
        {hasCreditCards ? (
          <div>
            {creditCards.map(card => (
              <div
                className='cursor-pointer'
                onClick={() => {
                  onCreditCardSelected(card.id);
                }}
                key={card.id}
              >
                <CreditCardView
                  cardObject={card}
                  leftNode={
                    <Radio
                      onChange={() => {
                        onCreditCardSelected(card.id);
                      }}
                      disabled={loading}
                      value={card.id}
                      size='small'
                      className='mr-2'
                      checked={card.id === cardId}
                    />
                  }
                />
              </div>
            ))}
            {AddPaymentBar}
          </div>
        ) : (
          !addCardFormVisible &&
          trialExpired && (
            <div>
              <div>No payment methods on file.</div>
              {AddPaymentBar}
            </div>
          )
        )}
        {paymentCardForm}
      </div>

      {startButton}
    </>
  );

  return (
    <div style={{ width: '536px' }}>
      {showAddPayment && addPaymentFormTitle}

      {paymentCardForm || paymentCards}
    </div>
  );
});
